import React, { useEffect, useState, useMemo } from "react";
import { Progress, Form, Button, Row, Col, Tag } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { TenantUsage, EditTenantLimits } from "./TenantUsageMonitor.types";
import StyledTable from "src/components/reusable/StyledTable";
import StatCard from "src/components/reusable/StatCard";
import { getTenantsDailyUsage } from "src/services/AdminPortalAPIs";
import EditTenantLimitsModal from "./EditTenantLimitsModal";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import StyledInput from "src/components/reusable/StyledInput";

const TenantUsageMonitor: React.FC = () => {
  const [data, setData] = useState<TenantUsage[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingTenant, setEditingTenant] = useState<TenantUsage | null>(null);
  const [form] = Form.useForm();

  const calculatePercentage = (used: number, limit: number): number =>
    limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  const capitalizeWords = (str: string): string =>
    str.replace(/\b\w/g, char => char.toUpperCase());

  const handleEditClick = (tenant: TenantUsage) => {
    setEditingTenant(tenant);
    form.setFieldsValue({
      tenant_id: tenant.tenant_id,
      chatbot_query_limit: tenant.chatbot_query_limit,
      chatbot_token_limit: tenant.chatbot_token_limit,
      email_quota: tenant.email_quota,
      template_generation_limit: tenant.template_generation_limit,
      template_token_limit: tenant.template_token_limit,
      social_listening_analysis_limit: tenant.social_listening_analysis_limit,
      social_listening_token_limit: tenant.social_listening_token_limit,
    });
    setEditModalVisible(true);
  };

  const handleSaveLimits = async () => {
    try {
      const values: EditTenantLimits = await form.validateFields();
      setEditModalVisible(false);
      setEditingTenant(null);
    } catch (err) {
      console.error("Validation failed:", err);
    }
  };

  const refreshData = () => {
    setLoading(true);
    getTenantsDailyUsage()
      .then(updatedData => setData(updatedData))
      .catch(err => console.error("Failed to refresh usage:", err))
      .finally(() => setLoading(false));
  };

  const filteredData = useMemo(() => {
    if (!searchText) {return data;}
    return data.filter(t =>
      t.tenant_name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [data, searchText]);

  // Decide tag + progress color
  const getUsageStatus = (
    pct: number
  ): { label: string; tagColor: string; progressColor: string } => {
    if (pct >= 90)
      {return { label: `${pct}%`, tagColor: "red", progressColor: "red" };}
    if (pct >= 50)
      {return { label: `${pct}%`, tagColor: "orange", progressColor: "orange" };}
    return { label: `${pct}%`, tagColor: "green", progressColor: "#1677ff" }; // default blue
  };

  const renderMetric = (used: number | undefined, limit: number | undefined) => {
    const safeUsed = Number.isFinite(Number(used)) ? Number(used) : 0;
    const safeLimit = Number.isFinite(Number(limit)) ? Number(limit) : 0;
    const pct = calculatePercentage(safeUsed, safeLimit);
    const status = getUsageStatus(pct);
    return (
      <div className="tum-metric-cell">
        <div className="tum-metric-top">
          <span>
            {safeUsed} / {safeLimit}
          </span>
          <Tag color={status.tagColor} bordered={true}>
            {status.label}
          </Tag>
        </div>
        <Progress
          percent={pct}
          size="small"
          showInfo={false}
          strokeColor={status.progressColor}
          className="tum-progress-bar"
        />
      </div>
    );
  };

  const columns = [
    {
      title: "Tenant Name",
      dataIndex: "tenant_name",
      key: "tenant_name",
      render: (text: string) => capitalizeWords(text),
    },
    {
      title: "Chatbot Queries",
      render: (_: any, record: TenantUsage) =>
        renderMetric(record.chatbot_queries_used, record.chatbot_query_limit),
    },
    {
      title: "Chatbot Tokens",
      render: (_: any, record: TenantUsage) =>
        renderMetric(record.chatbot_tokens_used, record.chatbot_token_limit),
    },
    {
      title: "Emails",
      render: (_: any, record: TenantUsage) =>
        renderMetric(record.emails_sent, record.email_quota),
    },
    {
      title: "Templates",
      render: (_: any, record: TenantUsage) =>
        renderMetric(
          record.templates_generated,
          record.template_generation_limit
        ),
    },
    {
      title: "Template Tokens",
      render: (_: any, record: TenantUsage) =>
        renderMetric(record.template_tokens_used, record.template_token_limit),
    },
    {
      title: "Social Listening",
      render: (_: any, record: TenantUsage) =>
        renderMetric(
          record.social_listening_analysis_used,
          record.social_listening_analysis_limit
        ),
    },
    {
      title: "Social Listening Tokens",
      render: (_: any, record: TenantUsage) =>
        renderMetric(
          record.social_listening_tokens_used,
          record.social_listening_token_limit
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: TenantUsage) => (
        <Tippy content="Edit" placement="top" animation="shift-away" duration={200}>
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditClick(record)}
        >
          Edit
        </Button>
        </Tippy>
      ),
    },
  ];

  useEffect(() => {
    setLoading(true);
    getTenantsDailyUsage()
      .then(data => setData(data))
      .catch(err => console.error("Failed to fetch usage:", err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="admin-portal-page">
      <span className="heading-primary">Quota Management</span>
      <Row gutter={16} className="tum-stat-row">
        <Col span={8}>
          <StatCard label="Total Tenants" value={data.length} />
        </Col>
        <Col span={8}>
          <StatCard
            label="Active Tenants"
            value={data.filter(t => t.chatbot_query_limit > 0).length}
          />
        </Col>
        <Col span={8}>
          <StatCard
            label="Critical Usage"
            value={
              data.filter(
                t =>
                  calculatePercentage(
                    t.chatbot_queries_used,
                    t.chatbot_query_limit
                  ) >= 90
              ).length
            }
          />
        </Col>
      </Row>

      {/* Search + Table */}
      <div className="tum-table-card">
        <StyledInput
          placeholder="Search tenants"
          onChange={e => setSearchText(e.target.value)}
          className="admin-search"
          style={{ marginBottom: "25px", marginTop: "10px" }}
        />

        <StyledTable<TenantUsage>
          columns={columns}
          dataSource={filteredData}
          rowKey="daily_usage_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          className="usage-table"
          tableLayout="fixed"
        />
      </div>
<EditTenantLimitsModal
  visible={editModalVisible}
  tenant={editingTenant}
  onCancel={() => setEditModalVisible(false)}
  onSuccess={() => {
    console.log("Refresh tenant table after update");
    setEditModalVisible(false);
    setEditingTenant(null);
  }}
/>

    </div>
  );
};

export default TenantUsageMonitor;
